import { useMemo } from "react";

import { JourneyStatus } from "src/types/journeys";
import type {
  JourneyState,
  JourneyTemplateState,
} from "src/pages/journeys/types";
import {
  type JourneysBoolExp,
  useScheduledJourneyStartsQuery,
} from "src/graphql";
import type { Schedule } from "src/components/schedule/types";
import type { JourneyNavigationLocation } from "src/pages/journeys/navigation/types";
import { LIVE_STATES } from "src/pages/journeys/constants";

export function shouldAutoEditDraftNode({
  journeyState,
  location,
}: {
  journeyState: JourneyState | JourneyTemplateState;
  location: JourneyNavigationLocation;
}): boolean {
  return (
    journeyState === "draft" &&
    location.type === "node-details" &&
    Boolean(location.nodeId)
  );
}

/** Whether the canvas Edit button edits the journey in place rather than staging a draft or staying disabled. */
export function canvasEditsInPlace({
  journeyState,
  isRealtimeJourney,
  stagedModeEnabled,
}: {
  journeyState: JourneyState | JourneyTemplateState;
  isRealtimeJourney: boolean;
  stagedModeEnabled: boolean;
}): boolean {
  if (journeyState === "resetting") return false;
  const liveStatusBlocksEditing = getLiveStatusBlocksEditing({
    isJourneyLive: LIVE_STATES.includes(journeyState),
    journeyState,
    isRealtimeJourney,
  });
  // With staged mode on, a paused warehouse journey edits through a staged draft.
  const stagesEdits =
    stagedModeEnabled && !isRealtimeJourney && journeyState === "paused";
  return !liveStatusBlocksEditing && !stagesEdits;
}

/** A link to a tile's section in edit mode opens the canvas in edit mode wherever the Edit button edits in place. */
export function shouldAutoEditLinkedSection({
  location,
  ...journey
}: Parameters<typeof canvasEditsInPlace>[0] & {
  location: JourneyNavigationLocation;
}): boolean {
  return (
    canvasEditsInPlace(journey) &&
    location.type === "node-details" &&
    location.section != null &&
    location.mode === "edit"
  );
}

/** The server lets an active realtime journey be edited in place; every other live journey is read-only. */
export const getLiveStatusBlocksEditing = ({
  isJourneyLive,
  journeyState,
  isRealtimeJourney,
}: {
  isJourneyLive: boolean;
  journeyState: JourneyState | JourneyTemplateState;
  isRealtimeJourney: boolean;
}): boolean =>
  isJourneyLive && !(isRealtimeJourney && journeyState === "active");

type GetJourneyState = {
  deleted?: boolean | null;
  reset?: boolean;
  status: JourneyStatus;
  numberOfUsersInJourney: number;
  numberOfPreviousRuns: number;
};

/**
 * Determines the visible state of the journey
 */
export const getJourneyState = ({
  deleted,
  reset,
  status,
  numberOfUsersInJourney,
  numberOfPreviousRuns,
}: GetJourneyState): JourneyState => {
  if (deleted) {
    return "deleting";
  }

  if (reset) {
    return "resetting";
  }

  // If staged, it's staged
  if (status === JourneyStatus.Staged) {
    return "staged";
  }

  // If enabled, it's active
  if (status === JourneyStatus.Enabled) {
    return "active";
  }

  if (status === JourneyStatus.Draining) {
    return "draining";
  }

  // No users are currently in the journey
  if (numberOfUsersInJourney === 0) {
    // No previous runs means it's a draft
    if (numberOfPreviousRuns === 0) {
      return "draft";
    }

    // If there are previous runs, it's off
    return "off";
  }

  // Otherwise, if there are users in the journey, it's paused
  return "paused";
};

/** Filter to find journeys where all nodes have zero users */
const NoUsersInJourneyFilter: JourneysBoolExp = {
  nodes_aggregate: {
    count: {
      filter: { number_users: { _gt: "0" } }, // Find nodes with non-zero users
      predicate: { _eq: 0 }, // Make sure there are no nodes with users (meaning all nodes have 0 users)
    },
  },
};

export const AllJourneyFilter: JourneysBoolExp = {
  is_latest: { _eq: true },
};

export const ActiveJourneyFilter: JourneysBoolExp = {
  is_latest: { _eq: true },
  status: {
    _in: [JourneyStatus.Enabled, JourneyStatus.Draining],
  },
  _or: [{ reset: { _eq: false } }, { reset: { _is_null: true } }],
};

export const InactiveJourneyFilter: JourneysBoolExp = {
  is_latest: { _eq: true },
  _not: {
    status: { _in: [JourneyStatus.Enabled, JourneyStatus.Draining] },
    _or: [{ reset: { _eq: false } }, { reset: { _is_null: true } }],
  },
};

export const DraftJourneyFilter: JourneysBoolExp = {
  is_latest: { _eq: true },
  _or: [{ reset: { _eq: false } }, { reset: { _is_null: true } }],
  status: { _eq: JourneyStatus.Disabled },
  ...NoUsersInJourneyFilter,
  // No previous runs
  runs_aggregate: {
    count: {
      filter: { simulation_id: { _is_null: true } },
      predicate: { _eq: 0 },
    },
  },
};

export const getUpcomingStartDate = (
  schedule: Pick<Schedule, "startDate"> | null | undefined,
  now = new Date(),
): string | null => {
  const startDate = schedule?.startDate;
  return startDate && new Date(startDate) > now ? startDate : null;
};

export const ScheduledJourneyFilter: JourneysBoolExp = {
  is_latest: { _eq: true },
  status: { _eq: JourneyStatus.Enabled },
  execution_mode: { _eq: "batch" },
  schedule: { _has_key: "startDate" },
  _not: { schedule: { _contains: { startDate: null } } },
};

export const useScheduledJourneyIds = (): string[] | undefined => {
  const { data } = useScheduledJourneyStartsQuery({
    filter: ScheduledJourneyFilter,
  });
  return useMemo(
    () =>
      data?.journeys
        .filter((journey) => getUpcomingStartDate(journey.schedule))
        .map((journey) => journey.id),
    [data],
  );
};

export const getActiveJourneyFilter = (
  scheduledJourneyIds: string[] | undefined,
): JourneysBoolExp => ({
  ...ActiveJourneyFilter,
  _not: { id: { _in: scheduledJourneyIds ?? [] } },
});

export const OffJourneyFilter: JourneysBoolExp = {
  is_latest: { _eq: true },
  _or: [{ reset: { _eq: false } }, { reset: { _is_null: true } }],
  status: { _eq: JourneyStatus.Disabled },
  ...NoUsersInJourneyFilter,
  // At least 1 previous run
  runs_aggregate: {
    count: {
      filter: { simulation_id: { _is_null: true } },
      predicate: { _gt: 0 },
    },
  },
};

export const PausedJourneyFilter: JourneysBoolExp = {
  is_latest: { _eq: true },
  _or: [{ reset: { _eq: false } }, { reset: { _is_null: true } }],
  status: { _eq: JourneyStatus.Disabled },
  // At least 1 previous run
  runs_aggregate: {
    count: {
      filter: { simulation_id: { _is_null: true } },
      predicate: { _gt: 0 },
    },
  },
  // At least 1 user in the journey
  nodes_aggregate: {
    count: {
      filter: { number_users: { _gt: "0" } },
      predicate: { _gt: 0 },
    },
  },
};
